import { useTranslation } from 'react-i18next';
import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { type ReactElement, type ReactNode, useState } from 'react';
import { Button } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';

export type ConfirmPopoverProps = {
  /** The button that asks; opens the popover. */
  children: ReactElement;
  /** What is about to happen ("Remove this rate?"). */
  message: ReactNode;
  confirmLabel: string;
  /** Runs on confirm; the popover closes when it settles, and stays open showing the error when it fails. */
  onConfirm(): Promise<void>;
};

/**
 * Asks before a destructive action right where it was pressed: a short
 * question with Cancel (focused first, so Enter doesn't destroy) and the
 * action.
 */
export function ConfirmPopover({
  children,
  message,
  confirmLabel,
  onConfirm
}: ConfirmPopoverProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ConfirmPopover' });
  const [open, setOpen] = useState(false);
  const mutation = promiseState.useMutation(async () => {
    await onConfirm();
    setOpen(false);
  });
  const pending = mt.isInProgress(mutation.state);
  return (
    <Popover
      open={open}
      onOpenChange={next => {
        if (pending) return;
        setOpen(next);
        if (!next) mutation.reset();
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        role="alertdialog"
        aria-label={confirmLabel}
        className="flex w-64 flex-col gap-3 p-3"
      >
        <p className="text-base text-fg-default">{message}</p>
        {mt.isInError(mutation.state) && (
          <p role="alert" className="text-sm text-fg-danger">
            {mutation.state.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            autoFocus
            disabled={pending}
            onClick={() => setOpen(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={pending}
            // The error shows in the popover; it needs no second report.
            onClick={() => void mutation.track(undefined).catch(() => {})}
          >
            {pending && <LoadingSpinner />}
            {confirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
